/**
 * 应用主体框架
 * @author jasonelchen
 */
import React, { FunctionComponent, useMemo, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Switch, Route, matchPath, useLocation } from 'react-router-dom'
import { BackTop, ConfigProvider, Spin } from 'antd'
import classNames from 'classnames'
import zhCN from 'antd/es/locale/zh_CN'
import moment from 'moment';
import { SiderBar, Login, ErrorBoundary, ConfigInfo } from '@components'
import { sessionStore, t3cStore } from '@store'
import { useAppConfigs } from '@hooks'
import InjectAxiosInterceptors from './library/InjectAxiosInterceptors'
import { TableIdEnum } from '@configs/table'
import { sStorage } from '@library'
import CallWorkbench from './components/CallWorkbench'
import AiCallingEntry from './components/AiCallingEntry'
import NotificationBanner from './components/NotificationBanner'
import { useShallow } from 'zustand/shallow'
import { registerT3cExtendedListeners } from '@store/t3c-listeners'
import('moment/dist/locale/zh-cn' as any)
moment.locale('zh-cn');
const Frame: FunctionComponent<React.PropsWithChildren<{}>> = () => {
  const location = useLocation()
  const { loading, menu, routes } = useAppConfigs()
  const { getModuleCompanyMap, userInfo } = sessionStore(
    useShallow(s => ({
      getModuleCompanyMap: s.getModuleCompanyMap,
      userInfo: s.userInfo,
    }))
  )
  const { t3cReady } = t3cStore(
    useShallow(s => ({
      t3cReady: s.t3cReady,
    }))
  )
  useEffect(() => {
    getModuleCompanyMap()
  }, [])
  useEffect(() => {
    if (!t3cReady) return
    registerT3cExtendedListeners()
  }, [t3cReady, userInfo])

  useEffect(() => {
    // 清除对应列表页缓存的筛选数据
    // 应用场景：
    //   部分页面需要进入详情页，需要保留筛选条件
    //   当缓存了筛选数据之后，除了切换对应页面之外，切换到其他页面，需清除筛选值
    const needClearSessionPath = {
      [TableIdEnum.TCCC_CALL_RECORD]: ['/task/call-record', '/task/score-detail'],
      [TableIdEnum.MEETING_CALL_RECORD]: ['/task/call-record', '/task/score-detail'],
    }
    Object.keys(needClearSessionPath).forEach(key => {
      if (location?.pathname && !needClearSessionPath?.[key]?.includes(location.pathname)) {
        // 筛选组件【CommonFilters】应用这个
        sStorage.remove(`${key}_FILTER_VALUES`)
        // 这个如果详情页涉及【上一条/下一条】会用到
        sStorage.remove(`${key}_FILTER`)
      }
    })
  }, [location])

  const children = useMemo(() => (
    <Switch>
      {routes.map(route => {
        const { key, ...routeProps } = route;
        return <Route key={key} strict {...routeProps} />;
      })}
    </Switch>
  ), [routes])

  const matchedRoute = useMemo(() => routes.find(route => matchPath(location.pathname, route)), [routes, location])
  const { key, standalone, needLogin } = matchedRoute || {}

  if (!matchedRoute) {
    return (
      <Login gettingAuth={loading}>
        <div id="main-app" className={classNames('main-app', 'page-not-found')}>
          {children}
        </div>
      </Login>
    )
  }

  const $page = (
    <>
      {needLogin && <NotificationBanner />}
      {!standalone && <SiderBar menus={menu} />}
      <div id="main-app" className={classNames('main-app', `page-${key}`)}>
        <BackTop target={() => document.getElementById('main-app')} style={{ bottom: 20 }} />
        <Suspense fallback={<div className="login-loading"><Spin spinning tip="加载中..." /></div>}>
          {children}
          <CallWorkbench />
        </Suspense>
      </div>
      {needLogin && <AiCallingEntry />}
    </>
  )

  return needLogin ? (
    <Login>
      {$page}
    </Login>
  ) : $page
}

const App: FunctionComponent<React.PropsWithChildren<{}>> = () => {
  return (
    <ConfigProvider locale={zhCN}>
      <BrowserRouter basename="/">
        <ErrorBoundary>
          <InjectAxiosInterceptors />
          <Frame />
          <ConfigInfo />
        </ErrorBoundary>
      </BrowserRouter>
    </ConfigProvider>
  )
}

export default App
